import { useEffect, useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import { supabase } from '@/lib/supabase';
import CommandSidebar from './components/CommandSidebar';
import { COMMAND_MODULES } from './lib/constants';

const PASSWORD_KEY = 'hop_admin_password';

export default function PainelShell() {
  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loggingIn, setLoggingIn] = useState(false);

  useSEO({
    title: 'Centro de Comando | HomeOffice Premium',
    description:
      'Visão central dos sistemas da HomeOffice Premium: CRM, oportunidades, projetos, licitações, clientes e inteligência.',
    keywords: 'painel, crm, licitacoes, homeoffice premium, gestao',
    path: '/painel',
  });

  useEffect(() => {
    const saved = sessionStorage.getItem(PASSWORD_KEY);
    if (saved) {
      setPassword(saved);
      setAuthed(true);
    }
    setCheckingAuth(false);
  }, []);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoggingIn(true);
    setLoginError(null);
    try {
      const { data, error } = await supabase.functions.invoke('order-admin', {
        body: { action: 'verify', password: password.trim() },
      });
      if (error) throw error;
      const parsed = data as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      sessionStorage.setItem(PASSWORD_KEY, password.trim());
      setAuthed(true);
    } catch {
      setLoginError('Senha incorreta. Tente novamente.');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(PASSWORD_KEY);
    setPassword('');
    setAuthed(false);
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#F6F6F4] flex items-center justify-center px-4">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-sm bg-white rounded-2xl border border-gray-200 p-8"
        >
          <div className="w-14 h-14 flex items-center justify-center bg-[#E8610A] text-white rounded-xl mx-auto mb-4">
            <i className="ri-command-line text-2xl"></i>
          </div>
          <h1 className="text-xl font-bold text-gray-900 text-center mb-1">
            HomeOffice Premium
          </h1>
          <p className="text-sm text-gray-500 text-center mb-6">
            Centro de Comando · acesso restrito
          </p>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
            Senha
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Digite sua senha"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 transition-all"
          />
          {loginError && (
            <p className="text-sm text-red-600 mt-3">{loginError}</p>
          )}
          <button
            type="submit"
            disabled={loggingIn}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#E8610A] text-white text-sm font-semibold px-5 py-3 rounded-xl hover:bg-[#C4500A] transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60 mt-4"
          >
            {loggingIn ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Entrando...
              </>
            ) : (
              <>
                <i className="ri-login-box-line"></i>
                Entrar
              </>
            )}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F6F4] flex">
      <CommandSidebar onLogout={handleLogout} />

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar mobile */}
        <header className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center bg-[#E8610A] text-white rounded-lg">
              <i className="ri-command-line text-base"></i>
            </div>
            <span className="font-bold text-sm text-gray-900">
              HomeOffice Premium
            </span>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 px-2 py-1.5 rounded-lg whitespace-nowrap cursor-pointer"
          >
            <i className="ri-logout-box-r-line"></i>
            Sair
          </button>
        </header>

        {/* Navegação mobile */}
        <nav className="md:hidden bg-white border-b border-gray-200 px-2 py-2 flex items-center gap-1 overflow-x-auto">
          {COMMAND_MODULES.map((m) => (
            <NavLink
              key={m.to}
              to={m.to}
              end={m.end}
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors duration-150 cursor-pointer ${
                  isActive
                    ? 'bg-[#E8610A] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`
              }
            >
              <i className={m.icon}></i>
              {m.label}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 px-4 md:px-8 py-6 md:py-8">
          <div className="max-w-7xl mx-auto w-full">
            <Outlet context={{ password }} />
          </div>
        </main>
      </div>
    </div>
  );
}